import { useEffect, useSyncExternalStore } from 'react'
import { makeThumbnail } from './imageDownscale'

/**
 * Preview thumbnails of the files being uploaded, shared by the dialog, the progress list and the
 * floating pill. The page never renders an original file: a 40 MP photo decodes to ~200 MB, and
 * twenty of them would bring the machine to its knees. Each thumbnail is a few KB, made in the
 * worker, and kept while some component shows it; a short grace period covers the dialog
 * switching from the form to the progress list, then the object URL is revoked.
 */

type Entry = { url: string | null; state: 'pending' | 'ready' | 'failed'; refs: number; release?: ReturnType<typeof setTimeout> }

const RELEASE_AFTER_MS = 30_000
const entries = new Map<File, Entry>()
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((listener) => listener())

const drop = (file: File) => {
  const entry = entries.get(file)
  if (!entry || entry.refs > 0) return
  if (entry.url) URL.revokeObjectURL(entry.url)
  entries.delete(file)
  emit()
}

const retain = (file: File) => {
  let entry = entries.get(file)
  if (!entry) {
    const created: Entry = { url: null, state: 'pending', refs: 0 }
    entry = created
    entries.set(file, created)
    void makeThumbnail(file).then((blob) => {
      if (entries.get(file) !== created) return
      created.url = blob ? URL.createObjectURL(blob) : null
      created.state = blob ? 'ready' : 'failed'
      // Released while it was being made: let the grace period run from now.
      if (created.refs === 0 && !created.release) created.release = setTimeout(() => drop(file), RELEASE_AFTER_MS)
      emit()
    })
  }
  if (entry.release) { clearTimeout(entry.release); entry.release = undefined }
  entry.refs += 1
}

const release = (file: File) => {
  const entry = entries.get(file)
  if (!entry) return
  entry.refs = Math.max(0, entry.refs - 1)
  if (entry.refs === 0 && !entry.release) entry.release = setTimeout(() => { entry.release = undefined; drop(file) }, RELEASE_AFTER_MS)
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

/** The thumbnail of `file` while the component is mounted: `url` stays null until it is ready. */
export const useFileThumbnail = (file: File) => {
  useEffect(() => {
    retain(file)
    return () => release(file)
  }, [file])
  const url = useSyncExternalStore(subscribe, () => entries.get(file)?.url ?? null)
  const failed = useSyncExternalStore(subscribe, () => entries.get(file)?.state === 'failed')
  return { url, failed }
}
